import { Link } from 'react-router-dom'

import { Avatar, ChevronIcon } from '@/components/icons'
import { StatusPill } from '@/components/ui/StatusPill'
import { TimeBar, TimeLeft } from '@/features/live/Countdown'
import { reasonOf, subjectOf } from '@/features/live/live'
import { cn } from '@/lib/utils'
import type { LiveItem } from '@/types/api'

/**
 * The queue as rows: whose check it is, what for, who is on it, and how long
 * is left. The whole row opens the solve screen. `current` marks the one
 * already open (the "up next" list beside it).
 */
export function LiveQueueList({ items, current, dense }: { items: LiveItem[]; current?: string; dense?: boolean }) {
  return (
    <ul className="divide-y divide-line">
      {items.map((item) => {
        const subject = subjectOf(item)
        const who = item.user?.fullName || item.user?.email || 'Unknown user'
        const active = item.handoffId === current
        return (
          <li key={item.handoffId}>
            <Link
              to={`/live/${encodeURIComponent(item.handoffId)}`}
              aria-current={active ? 'true' : undefined}
              className={cn(
                'group relative flex items-center gap-3 px-4 transition-colors hover:bg-surface-muted sm:px-5',
                dense ? 'py-2.5' : 'py-3.5',
                active && 'bg-accent-soft hover:bg-accent-soft',
              )}
            >
              {!dense && <Avatar name={who} />}
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2">
                  <p className="min-w-0 truncate text-[14px] font-medium text-ink">{who}</p>
                  <StatusPill tone={item.reason === 'sms' ? 'neutral' : 'warning'} live size="sm">
                    {reasonOf(item.reason).label}
                  </StatusPill>
                </div>
                <p className="mt-0.5 truncate text-[12.5px] text-ink-muted">
                  {subject.title}
                  {subject.detail && <span className="text-ink-faint"> · {subject.detail}</span>}
                </p>
                {item.assistant && (
                  <p className="mt-0.5 truncate text-[12px] text-info">{item.assistant.you ? 'You are on this one' : `${item.assistant.name} is on this one`}</p>
                )}
                <TimeBar remaining={item.remainingSeconds} timeout={item.timeoutSeconds} className="mt-2" />
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <TimeLeft remaining={item.remainingSeconds} waited={item.waitedSeconds} />
                {!dense && (
                  <span className="hidden h-9 items-center rounded-field bg-ink px-3.5 text-[13px] font-medium text-oncolor transition-colors group-hover:bg-ink/90 sm:inline-flex">
                    Solve
                  </span>
                )}
                <ChevronIcon className={cn('h-4 w-4 text-ink-faint', !dense && 'sm:hidden')} />
              </div>
            </Link>
          </li>
        )
      })}
    </ul>
  )
}
